/**
 * Base de datos de frases estoicas y filosofía de alto rendimiento deportivo.
 * Rotación diaria dinámica que garantiza frescura y reflexión estoica diaria.
 */

export const STOIC_SPORTS_QUOTES = [
  "Ningún atleta vence sin antes haber luchado. La dificultad muestra de qué estás hecho.",
  "No hay árbol recio ni consistente sino aquel que el viento azota con frecuencia.",
  "Lo que frena la acción, avanza la acción. Lo que se interpone en el camino se convierte en el camino.",
  "Te juzgo desafortunado porque nunca has vivido en la adversidad. Has pasado por la vida sin un oponente.",
  "Sufre el dolor de la disciplina hoy para no sufrir el dolor del arrepentimiento mañana.",
  "No pidas una carga más ligera, pide unos hombros más fuertes.",
  "La mente que se adapta y convierte todo obstáculo en combustible es invencible.",
  "Primero dite a ti mismo lo que serías; y luego haz lo que tienes que hacer.",
  "Un atleta no se mide cuando el viento sopla a favor, sino cuando la tormenta aprieta.",
  "La suerte es lo que ocurre cuando la preparación meticulosa se encuentra con la oportunidad.",
  "El cuerpo debe obedecer a la mente sin dudar, sin importar el ardor de la fatiga.",
  "Cuanto más sudas en el entrenamiento silencioso, menos sangras en la competición.",
  "No te domina el cansancio físico; te domina la mente que le da permiso al cansancio.",
  "No puedes controlar el viento ni la lluvia; solo puedes controlar tu ritmo y tu esfuerzo.",
  "La constancia diaria es el verdadero triunfo estoico sobre la pereza y las excusas.",
  "Roma no se construyó en un día, pero ponían ladrillos cada hora. Sigue sumando kilómetros.",
  "El dolor físico es temporal, pero la satisfacción de mantener tu palabra es eterna.",
  "Examina tus hábitos diarios: lo que haces con constancia es exactamente en lo que te conviertes.",
  "La tranquilidad estoica es la victoria sobre el caos de la mente en pleno pico de esfuerzo.",
  "El hierro solo se forja bajo el fuego más intenso. Acepta el ardor del entrenamiento.",
  "No esperes que las condiciones sean perfectas. El verdadero atleta entrena bajo cualquier cielo.",
  "Dominar a otros es fuerza; dominarte a ti mismo en cada repetición es verdadero poder.",
  "Céntrate en el proceso, no en el resultado. La velocidad vendrá como consecuencia de tu disciplina.",
  "Si te rindes hoy, el esfuerzo acumulado de ayer no servirá de nada. Mantén la línea de meta.",
  "El coraje no es la ausencia de fatiga, sino el dominio absoluto sobre ella.",
  "No hay atajos hacia la maestría física. La repetición disciplinada es la madre de la excelencia.",
  "El sudor del entrenamiento de hoy es la reserva de energía para los momentos críticos de mañana.",
  "Soporta y abstente: soporta el dolor del esfuerzo exigente y abstente de las excusas fáciles.",
  "Cualquier deportista mantiene el ritmo cuando es fácil. El atleta estoico acelera cuando cuesta.",
  "Acepta lo que no puedes cambiar, ajusta tu entrenamiento a la realidad y sigue pedaleando.",
  "El descanso es tan sagrado como el esfuerzo. Recupera con la misma disciplina con la que entrenas.",
  "No compitas para superar a tus rivales; entrena para superar a tu versión de ayer.",
  "La mente firme convierte la pendiente más dura en un escalón hacia la cima de tu rendimiento.",
  "No dejes que la incomodidad de los últimos minutos te robe la grandeza de todo el trabajo previo.",
  "Cada amanecer te da una nueva oportunidad para demostrar de qué está hecho tu carácter.",
  "La disciplina es hacer lo que debes hacer, cuando debes hacerlo, te apetezca o no.",
  "El cuerpo resiste casi cualquier estímulo; es a la mente a la que hay que educar cada día.",
  "Ningún obstáculo externo es tan grande como la duda que albergas en tu propia cabeza.",
  "El verdadero carácter del atleta se revela en el último kilómetro, cuando nadie le está mirando.",
  "No busques entrenamientos fáciles; busca la fortaleza para dominar sesiones difíciles.",
  "La excelencia no es un acto aislado, sino un hábito grabado a fuego en cada sesión.",
  "Tu único límite real es la historia que te cuentas a ti mismo mientras estás sufriendo.",
  "Abraza el ardor de la zona alta de pulso: ahí es donde ocurre la adaptación fisiológica real.",
  "El tiempo pasa de todos modos; tú decides si lo usas para avanzar o para estancarte.",
  "La fuerza no proviene de la capacidad física, sino de una voluntad indomable.",
  "Visualiza el objetivo, acepta el costo físico del camino y ejecuta tu plan sin vacilar.",
  "No te quejes del viento en contra; ajusta tu postura aerodinámica y sigue apretando.",
  "La consistencia silenciosa hace más ruido con el tiempo que mil promesas no cumplidas.",
  "Cae siete veces, levántate ocho. El rendimiento de élite se construye sobre la resiliencia.",
  "Haz hoy lo que otros no harán para conseguir mañana los resultados que otros no conseguirán.",
  "Tu cuerpo escucha todo lo que dice tu mente. Aliméntala con pensamientos de poder y enfoque.",
  "La victoria más noble y trascendente para un deportista es la victoria sobre sí mismo."
];

/**
 * Retorna una frase estoica de alto rendimiento deportivo, rotando diariamente sin repetición.
 */
export const getLocalQuote = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed
  const day = date.getDate();

  // 1. Frase especial de Año Nuevo
  if (day === 1 && month === 0) {
    return "365 páginas en blanco. Cada entrenamiento de hoy define tu temporada anual. ¡A por ello!";
  }

  // 2. Cálculo del día del año (1-366)
  const startOfYear = new Date(year, 0, 0);
  const diff = date - startOfYear;
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);

  // Algoritmo determinista diario con salto primo para distribuir la variabilidad entre días consecutivos
  const index = (dayOfYear * 17 + year * 7) % STOIC_SPORTS_QUOTES.length;

  return STOIC_SPORTS_QUOTES[index];
};

/**
 * Retorna un clima dinámico y sugerente simulado para entrenamientos al aire libre según mes e iluminación diaria.
 */
export const getSimulatedWeather = (date = new Date()) => {
  const month = date.getMonth();
  const hour = date.getHours();

  let temp;
  let status;
  let icon;
  let tip;

  // Verano (Jun-Ago)
  if (month >= 5 && month <= 7) {
    if (hour >= 11 && hour <= 18) {
      temp = 32;
      status = "Caluroso";
      icon = "☀️";
      tip = "Calor alto. ¡Foco en hidratación!";
    } else {
      temp = 22;
      status = "Fresco y Despejado";
      icon = "🌅";
      tip = "Hora fresca. Perfecto para sumar km";
    }
  }
  // Invierno (Dic-Feb)
  else if (month === 11 || month <= 1) {
    if (hour >= 19 || hour <= 7) {
      temp = 6;
      status = "Frío y Despejado";
      icon = "❄️";
      tip = "Frío. Valora rodillo o abrígate bien";
    } else {
      temp = 11;
      status = "Fresco e Intervalos";
      icon = "⛅";
      tip = "Fresco. Activa bien las articulaciones";
    }
  }
  // Otoño (Sep-Nov)
  else if (month >= 8 && month <= 10) {
    if (hour >= 18 || hour <= 7) {
      temp = 12;
      status = "Fresco y Humedad";
      icon = "🌫️";
      tip = "Humedad alta. Atención al calentamiento";
    } else {
      temp = 15;
      status = "Nublado";
      icon = "☁️";
      tip = "Fresco. Clima rápido para series de carrera";
    }
  }
  // Primavera (Mar-May)
  else {
    temp = 20;
    status = "Despejado";
    icon = "☀️";
    tip = "Clima perfecto. Suma volumen";
  }

  return { temp, status, icon, tip };
};
